import type { PeerId, Transport } from './transport';

/**
 * PEERS, VIA A LAMP-POST.
 *
 * Two browsers cannot talk to each other. That is not a limitation of this
 * project, it is the web: there is no inbound socket, and the only true
 * peer-to-peer option, WebRTC, needs a signalling server, a STUN server, and a
 * TURN server for the third of connections that cannot be traversed — which is
 * three pieces of infrastructure to avoid running one.
 *
 * So the browser build relays. Every message goes to a small server that copies
 * it to the addressee and forgets it: no game state, no validation, no
 * simulation. That distinction matters, because it is what keeps the promise in
 * GOAL.md intact. The host is still authoritative and the round still lives and
 * dies on a player's machine; the relay is a lamp-post that two people are
 * shouting past, and replacing it with Steam's relay network in the Electron
 * build changes this file and nothing else.
 *
 * THE ENVELOPE. Every frame on the wire is
 *
 *     [u8 addr][u8 type][payload...]
 *
 * where `addr` is the destination slot on the way up and is rewritten by the
 * relay to the sender's slot on the way down — so a client never has to trust a
 * "from" field that another client wrote. `type` is 0 for binary and 1 for UTF-8
 * text, which keeps the game's two encodings distinguishable without a second
 * WebSocket or a header the game layer has to know about.
 */

/** Reserved addresses in the envelope's first byte. */
const ADDR_ALL = 255;
const ADDR_RELAY = 254;

const TYPE_BIN = 0;
const TYPE_TEXT = 1;

export interface WsOptions {
  /** `wss://host/room/ABCD`. */
  url: string;
  /** Ask to be the host. The relay refuses if the room already has one. */
  createRoom: boolean;
  /** Called once the relay has assigned a slot and said whether we are host. */
  onReady?: (self: PeerId, isHost: boolean) => void;
}

/**
 * The intent to host travels in the URL, not in a first message.
 *
 * A Durable Object has to pick a slot while it is still deciding whether to
 * accept the upgrade at all — there is no "connected but not yet joined" state
 * to sit in — so the answer has to be there before the socket opens. Doing it
 * the same way against the dev relay keeps one handshake rather than two.
 */
function joinUrl(url: string, createRoom: boolean): string {
  return createRoom ? `${url}${url.includes('?') ? '&' : '?'}host=1` : url;
}

/** What the relay says. Small, JSON, and never seen by the game layer. */
type RelayMsg =
  | { t: 'joined'; slot: number; host: boolean; peers: number[] }
  | { t: 'peer'; slot: number }
  | { t: 'gone'; slot: number; reason: string }
  | { t: 'full' }
  | { t: 'nohost' };

export function slotId(slot: number): PeerId {
  return `p${slot}`;
}
function slotOf(id: PeerId): number {
  return Number(id.slice(1));
}

export class WebSocketTransport implements Transport {
  readonly stats = { sent: 0, recv: 0, sentMsgs: 0, recvMsgs: 0 };
  self: PeerId = 'p?';
  isHost = false;
  open = false;
  onJoin?: (id: PeerId) => void;
  onLeave?: (id: PeerId, reason: string) => void;
  onMessage?: (from: PeerId, data: ArrayBuffer | string) => void;
  onError?: (message: string) => void;

  private readonly ws: WebSocket;
  private readonly list: PeerId[] = [];
  private readonly ready?: (self: PeerId, isHost: boolean) => void;
  private readonly enc = new TextEncoder();
  private readonly dec = new TextDecoder();

  constructor(opts: WsOptions) {
    this.ready = opts.onReady;
    this.ws = new WebSocket(joinUrl(opts.url, opts.createRoom));
    this.ws.binaryType = 'arraybuffer';
    this.ws.onmessage = (ev) => this.receive(ev.data as ArrayBuffer);
    this.ws.onerror = () => this.onError?.('connection failed');
    this.ws.onclose = (ev) => {
      this.open = false;
      // 1000 is a clean close we asked for; anything else is the round ending
      // underneath the player, which the UI has to say out loud.
      if (ev.code !== 1000) this.onError?.(ev.reason || 'disconnected');
    };
  }

  get peers(): readonly PeerId[] {
    return this.list;
  }

  send(to: PeerId, data: ArrayBuffer | string): void {
    if (this.ws.readyState !== WebSocket.OPEN) return;
    const frame = this.wrap(slotOf(to), data);
    this.stats.sent += frame.byteLength;
    this.stats.sentMsgs++;
    this.ws.send(frame);
  }

  broadcast(data: ArrayBuffer | string): void {
    if (this.ws.readyState !== WebSocket.OPEN) return;
    // One frame to the relay, fanned out there. Sending N copies from a client on
    // a domestic uplink is exactly the wrong place to spend upstream bandwidth.
    const frame = this.wrap(ADDR_ALL, data);
    this.stats.sent += frame.byteLength;
    this.stats.sentMsgs++;
    this.ws.send(frame);
  }

  close(): void {
    this.open = false;
    if (this.ws.readyState <= WebSocket.OPEN) this.ws.close(1000, 'bye');
  }

  // -----------------------------------------------------------------------

  private wrap(addr: number, data: ArrayBuffer | string): ArrayBuffer {
    const bytes = typeof data === 'string' ? this.enc.encode(data) : new Uint8Array(data);
    const out = new Uint8Array(2 + bytes.length);
    out[0] = addr;
    out[1] = typeof data === 'string' ? TYPE_TEXT : TYPE_BIN;
    out.set(bytes, 2);
    return out.buffer;
  }

  private receive(buf: ArrayBuffer): void {
    this.stats.recv += buf.byteLength;
    this.stats.recvMsgs++;
    const head = new Uint8Array(buf, 0, 2);
    const body = buf.slice(2);
    if (head[0] === ADDR_RELAY) {
      this.relay(JSON.parse(this.dec.decode(body)) as RelayMsg);
      return;
    }
    const from = slotId(head[0]);
    this.onMessage?.(from, head[1] === TYPE_TEXT ? this.dec.decode(body) : body);
  }

  private relay(m: RelayMsg): void {
    switch (m.t) {
      case 'joined': {
        this.self = slotId(m.slot);
        this.isHost = m.host;
        this.open = true;
        for (const s of m.peers) this.list.push(slotId(s));
        this.ready?.(this.self, this.isHost);
        // Announced after `ready`, so whoever wired the handlers in the callback
        // still hears about everyone already in the room.
        for (const s of m.peers) this.onJoin?.(slotId(s));
        break;
      }
      case 'peer':
        if (!this.list.includes(slotId(m.slot))) this.list.push(slotId(m.slot));
        this.onJoin?.(slotId(m.slot));
        break;
      case 'gone': {
        const id = slotId(m.slot);
        const i = this.list.indexOf(id);
        if (i >= 0) this.list.splice(i, 1);
        this.onLeave?.(id, m.reason);
        break;
      }
      case 'full':
        this.onError?.('that room is full');
        break;
      case 'nohost':
        this.onError?.('nobody is hosting that room');
        break;
    }
  }
}

/** Shared with the relay implementations so the envelope is defined once. */
export const ENVELOPE = { ADDR_ALL, ADDR_RELAY, TYPE_BIN, TYPE_TEXT };
